import store from '../../state';
import { FoliageService } from '../../services/FoliageService';
import { getElevationAt } from '../../services/PoiDataService';

export class FoliagePanel {
  private lastPoiKey: string = '';
  private currentElevation: number = 800;
  private container: HTMLElement | null = null;
  public onClose?: () => void;

  public setOnClose(cb: () => void) {
    this.onClose = cb;
  }

  public render(state: any): string {
    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const elev = poi && poi.elev ? poi.elev : this.currentElevation;
    const elevMeters = elev / 3.28084;
    const defaultInfo = FoliageService.getDefaultDateInfo();
    const monthIndex = state.foliageMonthIndex !== undefined ? state.foliageMonthIndex : defaultInfo.monthIndex;
    const selectedDateStr = state.foliageSelectedDate || defaultInfo.dateStr;
    const viewMode = state.foliageViewMode || 'peak_date';
    const treedOnly = !!state.foliageTreedOnly;
    const year = 2026;

    const locKey = `${lat.toFixed(4)}_${lon.toFixed(4)}`;
    if (locKey !== this.lastPoiKey) {
      this.lastPoiKey = locKey;
      if (poi && poi.elev && poi.elev > 0) {
        this.currentElevation = poi.elev;
      } else {
        getElevationAt(lat, lon).then(prof => {
          if (prof && prof.elevation > 0 && prof.elevation !== this.currentElevation) {
            this.currentElevation = prof.elevation;
            if (poi) {
              store.update({ selectedPoi: { ...poi, elev: prof.elevation } });
            } else {
              this.updateUi();
            }
          }
        });
      }
    }

    const { days, profile, firstDayCol, monthName } = FoliageService.getMonthCalendar(
      monthIndex,
      lat,
      lon,
      elevMeters,
      year
    );

    const monthTabLabels = ['Sep', 'Oct', 'Nov'];
    const monthHeaderDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Month navigation tabs HTML
    const tabsHtml = monthTabLabels.map((tabLabel, idx) => {
      const isActive = idx === monthIndex;
      return `
        <button 
          class="gw-foliage-month-tab ${isActive ? 'active' : ''}" 
          data-month-idx="${idx}"
          style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${isActive ? 'var(--color-primary, #ff9800)' : 'rgba(255,255,255,0.1)'}; background: ${isActive ? 'rgba(255, 152, 0, 0.2)' : 'rgba(15, 23, 42, 0.6)'}; color: ${isActive ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease;">
          ${tabLabel}
        </button>
      `;
    }).join('');

    // Calendar grid cells HTML
    let calendarGridHtml = `
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; font-size: 0.68rem; text-align: center; font-weight: 700; color: var(--text-muted); margin-bottom: 6px; width: 100%; box-sizing: border-box;">
        ${monthHeaderDays.map(d => `<div style="min-width: 0; box-sizing: border-box; text-align: center;">${d}</div>`).join('')}
      </div>
      <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; width: 100%; box-sizing: border-box;">
    `;

    // Blank padding cells
    for (let i = 0; i < firstDayCol; i++) {
      calendarGridHtml += `<div style="height: 48px; min-width: 0; background: rgba(255,255,255,0.02); border-radius: 6px; box-sizing: border-box;"></div>`;
    }

    // Day cells
    days.forEach(day => {
      const isSelected = day.dateStr === selectedDateStr;
      const activeBorder = isSelected ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.12)';
      const activeShadow = isSelected ? '0 0 10px rgba(255,255,255,0.4), inset 0 0 6px rgba(255,255,255,0.2)' : 'none';
      const todayIndicator = day.isToday ? `<span style="position: absolute; top: 2px; right: 3px; font-size: 0.55rem; color: #ffeb3b;">●</span>` : '';

      calendarGridHtml += `
        <div 
          class="gw-foliage-day-cell" 
          data-date="${day.dateStr}"
          title="${day.dateStr}: ${day.stageName} (${day.fpi}%) - Min Temp: ${day.tminC}°C"
          style="position: relative; height: 48px; min-width: 0; background: ${day.colorHex}; border-radius: 6px; border: ${activeBorder}; box-shadow: ${activeShadow}; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; padding: 3px 2px; box-sizing: border-box; transition: transform 0.15s ease, box-shadow 0.15s ease; user-select: none;">
          ${todayIndicator}
          <div style="font-size: 0.72rem; font-weight: 800; color: #ffffff; text-shadow: 0 1px 2px rgba(0,0,0,0.8); line-height: 1; padding-left: 2px;">
            ${day.dayNum}
          </div>
          <div class="gw-foliage-day-badge" style="font-size: 0.58rem; font-weight: 700; text-align: center; color: rgba(255,255,255,0.95); text-shadow: 0 1px 2px rgba(0,0,0,0.9); text-transform: uppercase; background: rgba(0,0,0,0.3); border-radius: 3px; padding: 1px 0; margin-top: 2px;">
            ${day.badgeLabel}
          </div>
        </div>
      `;
    });

    calendarGridHtml += `</div>`;

    const viewModeHtml = `
      <div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 2px; width: 100%;">
        <div style="display: grid; grid-template-columns: 1fr 1.35fr; gap: 4px; background: rgba(0,0,0,0.35); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); box-sizing: border-box;">
          <button class="gw-foliage-mode-btn" data-mode="peak_date" style="padding: 6px 2px; font-size: 0.68rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'peak_date' ? '#ffd54f' : 'transparent'}; background: ${viewMode === 'peak_date' ? 'rgba(255, 213, 79, 0.25)' : 'transparent'}; color: ${viewMode === 'peak_date' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            📅 Peak Date
          </button>
          <button class="gw-foliage-mode-btn" data-mode="forecast" style="padding: 6px 2px; font-size: 0.68rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'forecast' ? '#ff9800' : 'transparent'}; background: ${viewMode === 'forecast' ? 'rgba(255, 152, 0, 0.25)' : 'transparent'}; color: ${viewMode === 'forecast' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            🍁 Detailed Timing / Calendar
          </button>
        </div>
        <div style="background: rgba(0,0,0,0.35); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); box-sizing: border-box;">
          <button class="gw-foliage-mode-btn" data-mode="vibrancy" style="width: 100%; padding: 6px 4px; font-size: 0.70rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'vibrancy' ? '#e53935' : 'transparent'}; background: ${viewMode === 'vibrancy' ? 'rgba(229, 57, 53, 0.25)' : 'transparent'}; color: ${viewMode === 'vibrancy' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center; display: flex; align-items: center; justify-content: center; gap: 6px;">
            <span>✨</span> <span>Vibrancy This Season</span>
          </button>
        </div>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; background: rgba(0,0,0,0.35); padding: 3px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); box-sizing: border-box;">
          <button class="gw-foliage-mode-btn" data-mode="species" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'species' ? '#ab47bc' : 'transparent'}; background: ${viewMode === 'species' ? 'rgba(171, 71, 188, 0.25)' : 'transparent'}; color: ${viewMode === 'species' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            🌲 Species
          </button>
          <button class="gw-foliage-mode-btn" data-mode="heat_stress" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'heat_stress' ? '#f57c00' : 'transparent'}; background: ${viewMode === 'heat_stress' ? 'rgba(245, 124, 0, 0.25)' : 'transparent'}; color: ${viewMode === 'heat_stress' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            ☀️ Heat
          </button>
          <button class="gw-foliage-mode-btn" data-mode="soil_moisture" style="padding: 6px 2px; font-size: 0.66rem; font-weight: 700; border-radius: 5px; border: 1px solid ${viewMode === 'soil_moisture' ? '#0288d1' : 'transparent'}; background: ${viewMode === 'soil_moisture' ? 'rgba(2, 136, 209, 0.25)' : 'transparent'}; color: ${viewMode === 'soil_moisture' ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.15s ease; text-align: center;">
            💧 Moisture
          </button>
        </div>
      </div>
    `;

    // Mode-specific body content
    let modeContentHtml = '';
    if (viewMode === 'forecast') {
      modeContentHtml = `
        <!-- Map Coverage Scope Switcher -->
        <div style="display: flex; gap: 6px; width: 100%; margin-bottom: 2px;">
          <button 
            class="gw-foliage-coverage-btn ${!treedOnly ? 'active' : ''}" 
            data-coverage="landscape"
            title="Full landscape coverage across all North American land surfaces"
            style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${!treedOnly ? 'var(--color-primary, #ff9800)' : 'rgba(255,255,255,0.1)'}; background: ${!treedOnly ? 'rgba(255, 152, 0, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${!treedOnly ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🗺️</span> <span>Full Landscape</span>
          </button>
          <button 
            class="gw-foliage-coverage-btn ${treedOnly ? 'active' : ''}" 
            data-coverage="treed"
            title="Filter map to show only deciduous tree canopy regions"
            style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${treedOnly ? '#4caf50' : 'rgba(255,255,255,0.1)'}; background: ${treedOnly ? 'rgba(76, 175, 80, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${treedOnly ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🌲</span> <span>Treed Regions Only</span>
          </button>
        </div>

        <!-- Month Navigation Tabs -->
        <div style="display: flex; gap: 6px; width: 100%;">
          ${tabsHtml}
        </div>

        <!-- Interactive Calendar Grid -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-size: 0.78rem; font-weight: 700; color: #fff;">${monthName}</span>
            <span style="font-size: 0.65rem; color: var(--text-muted);">Calendar coloring reflects selected map location. Click calendar to view forecast for given day.</span>
          </div>
          ${calendarGridHtml}
        </div>

        <!-- Color Scale Legend -->
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px;">
          <div style="font-size: 0.68rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px; letter-spacing: 0.5px;">
            Foliage Progression Scale
          </div>
          <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #2e7d32 0%, #8bc34a 18%, #fbc02d 36%, #f57c00 54%, #d32f2f 72%, #795548 88%, #78909c 100%); margin-bottom: 5px; border: 1px solid rgba(255,255,255,0.12);"></div>
          <div style="display: grid; grid-template-columns: repeat(6, 1fr); font-size: 0.62rem; text-align: center; font-weight: 700;">
            <span style="color: #81c784;">Early</span>
            <span style="color: #fff176;">Moderate</span>
            <span style="color: #ffb74d;">Near Peak</span>
            <span style="color: #ef5350;">Peak</span>
            <span style="color: #bcaaa4;">Past</span>
            <span style="color: #b0bec5;">Bare</span>
          </div>
        </div>
      `;
    } else if (viewMode === 'canopy') {
      modeContentHtml = `
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 8px;">
          <div style="font-size: 0.78rem; font-weight: 700; color: #fff;">🌳 Deciduous Tree Canopy Cover</div>
          <div style="font-size: 0.68rem; color: var(--text-muted);">
            Shows percent density of broadleaf deciduous & mixed forest canopy from NLCD & NALCMS satellite observations.
          </div>
          <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(46,125,50,0.1) 0%, rgba(46,125,50,0.5) 40%, rgba(46,125,50,0.85) 75%, #1b5e20 100%); border: 1px solid rgba(255,255,255,0.1);"></div>
          <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: var(--text-muted); font-weight: 600;">
            <span>Sparse (&lt;15%)</span>
            <span>Moderate (40%)</span>
            <span>Dense Forest (&gt;80%)</span>
          </div>
        </div>
      `;
    } else if (viewMode === 'vibrancy') {
      modeContentHtml = `
        <!-- Map Coverage Scope Switcher -->
        <div style="display: flex; gap: 6px; width: 100%; margin-bottom: 2px;">
          <button 
            class="gw-foliage-coverage-btn ${!treedOnly ? 'active' : ''}" 
            data-coverage="landscape"
            title="Full landscape coverage across all North American land surfaces"
            style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${!treedOnly ? 'var(--color-primary, #ff9800)' : 'rgba(255,255,255,0.1)'}; background: ${!treedOnly ? 'rgba(255, 152, 0, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${!treedOnly ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🗺️</span> <span>Full Landscape</span>
          </button>
          <button 
            class="gw-foliage-coverage-btn ${treedOnly ? 'active' : ''}" 
            data-coverage="treed"
            title="Filter map to show only deciduous tree canopy regions"
            style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${treedOnly ? '#4caf50' : 'rgba(255,255,255,0.1)'}; background: ${treedOnly ? 'rgba(76, 175, 80, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${treedOnly ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🌲</span> <span>Treed Regions Only</span>
          </button>
        </div>

        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 0.78rem; font-weight: 700; color: #fff;">✨ Seasonal Color Vibrancy</div>
            <span style="font-size: 0.62rem; color: var(--text-muted); background: rgba(255,255,255,0.08); padding: 2px 6px; border-radius: 4px; font-weight: 600;">Departure from Typical</span>
          </div>
          <div style="font-size: 0.68rem; color: var(--text-muted); line-height: 1.4;">
            Anticipated difference in peak color brilliance and saturation compared to a typical autumn season across North America.
          </div>
          <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 2px;">
            <div style="display: flex; align-items: flex-start; gap: 8px;">
              <span style="display: inline-block; width: 20px; height: 14px; border-radius: 3px; background: #e53935; border: 1px solid rgba(255,255,255,0.25); flex-shrink: 0; margin-top: 2px;"></span>
              <div style="display: flex; flex-direction: column;">
                <span style="font-size: 0.72rem; font-weight: 700; color: #ff8a80;">Above Normal / Enhanced Vibrancy</span>
                <span style="font-size: 0.63rem; color: var(--text-muted); line-height: 1.3;">Plentiful sunshine & crisp 34-45°F nights promote rich red anthocyanin production and extended peak retention.</span>
              </div>
            </div>
            <div style="display: flex; align-items: flex-start; gap: 8px;">
              <span style="display: inline-block; width: 20px; height: 14px; border-radius: 3px; background: #ffb300; border: 1px solid rgba(255,255,255,0.25); flex-shrink: 0; margin-top: 2px;"></span>
              <div style="display: flex; flex-direction: column;">
                <span style="font-size: 0.72rem; font-weight: 700; color: #ffd54f;">Average / Typical Vibrancy</span>
                <span style="font-size: 0.63rem; color: var(--text-muted); line-height: 1.3;">Expected baseline autumn coloration matching 30-year regional climate averages.</span>
              </div>
            </div>
            <div style="display: flex; align-items: flex-start; gap: 8px;">
              <span style="display: inline-block; width: 20px; height: 14px; border-radius: 3px; background: #8d6e63; border: 1px solid rgba(255,255,255,0.25); flex-shrink: 0; margin-top: 2px;"></span>
              <div style="display: flex; flex-direction: column;">
                <span style="font-size: 0.72rem; font-weight: 700; color: #bcaaa4;">Below Normal / Reduced Vibrancy</span>
                <span style="font-size: 0.63rem; color: var(--text-muted); line-height: 1.3;">Unseasonably warm nights, summer drought scorch, cloudy rain, or premature hard freezes mute brilliance.</span>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (viewMode === 'peak_date') {
      modeContentHtml = `
        <!-- Map Coverage Scope Switcher -->
        <div style="display: flex; gap: 6px; width: 100%; margin-bottom: 2px;">
          <button 
            class="gw-foliage-coverage-btn ${!treedOnly ? 'active' : ''}" 
            data-coverage="landscape"
            title="Full landscape coverage across all North American land surfaces"
            style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${!treedOnly ? 'var(--color-primary, #ff9800)' : 'rgba(255,255,255,0.1)'}; background: ${!treedOnly ? 'rgba(255, 152, 0, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${!treedOnly ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🗺️</span> <span>Full Landscape</span>
          </button>
          <button 
            class="gw-foliage-coverage-btn ${treedOnly ? 'active' : ''}" 
            data-coverage="treed"
            title="Filter map to show only deciduous tree canopy regions"
            style="flex: 1; padding: 6px 4px; font-size: 0.72rem; font-weight: 700; border-radius: 6px; border: 1px solid ${treedOnly ? '#4caf50' : 'rgba(255,255,255,0.1)'}; background: ${treedOnly ? 'rgba(76, 175, 80, 0.25)' : 'rgba(15, 23, 42, 0.6)'}; color: ${treedOnly ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 5px;">
            <span>🌲</span> <span>Treed Regions Only</span>
          </button>
        </div>

        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-size: 0.78rem; font-weight: 700; color: #fff;">📅 Peak Color by Date</div>
            <span style="font-size: 0.62rem; color: var(--text-muted); background: rgba(255,255,255,0.08); padding: 2px 6px; border-radius: 4px; font-weight: 600;">First Day of Peak Color</span>
          </div>
          <div style="font-size: 0.68rem; color: var(--text-muted); line-height: 1.4;">
            Displays the projected calendar date when peak color arrives across North America. Locations peaking on or before Sept 15 appear green, transitioning chronologically through yellow, gold, orange, red, and brown to grey by November 15.
          </div>
          <div style="height: 12px; border-radius: 4px; background: linear-gradient(90deg, #2e7d32 0%, #8bc34a 17%, #fbc02d 33%, #f57c00 50%, #d32f2f 67%, #795548 83%, #78909c 100%); border: 1px solid rgba(255,255,255,0.18); margin-top: 2px;"></div>
          <div style="display: flex; justify-content: space-between; font-size: 0.62rem; font-weight: 700; color: var(--text-muted);">
            <span style="color: #4caf50;">Sept 15</span>
            <span style="color: #8bc34a;">Sept 25</span>
            <span style="color: #fbc02d;">Oct 5</span>
            <span style="color: #f57c00;">Oct 15</span>
            <span style="color: #ef5350;">Oct 25</span>
            <span style="color: #8d6e63;">Nov 5</span>
            <span style="color: #90a4ae;">Nov 15</span>
          </div>
          <div style="font-size: 0.64rem; color: var(--text-muted); line-height: 1.35; margin-top: 4px; background: rgba(255,255,255,0.03); padding: 6px 8px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.06);">
            💡 <strong>Interactive Telemetry:</strong> Click any location on the map to sample the exact projected peak arrival date.
          </div>
        </div>
      `;
    } else if (viewMode === 'heat_stress') {
      modeContentHtml = `
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 8px;">
          <div style="font-size: 0.78rem; font-weight: 700; color: #fff;">☀️ Summer Heat & Temp Anomaly (June 1 - Aug 31)</div>
          <div style="font-size: 0.68rem; color: var(--text-muted); line-height: 1.35;">
            Shows summer mean temperature departures (°C) against PRISM 30-year normals. Extreme heat stress accelerates chlorophyll breakdown and triggers premature leaf browning.
          </div>
          <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #1e40af 0%, #38bdf8 20%, #e2e8f0 38%, #fbbf24 55%, #f97316 70%, #dc2626 85%, #881337 100%); border: 1px solid rgba(255,255,255,0.12);"></div>
          <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: var(--text-muted); font-weight: 700;">
            <span style="color: #93c5fd;">-4°C</span>
            <span style="color: #e2e8f0;">0°C (Norm)</span>
            <span style="color: #fde047;">+1.5°C</span>
            <span style="color: #fb923c;">+3.0°C</span>
            <span style="color: #f87171;">+4.5°C</span>
            <span style="color: #f43f5e;">+6.0°C</span>
          </div>
        </div>
      `;
    } else if (viewMode === 'soil_moisture') {
      modeContentHtml = `
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 8px;">
          <div style="font-size: 0.78rem; font-weight: 700; color: #fff;">💧 Summer Soil Moisture Anomaly (June 1 - Aug 31)</div>
          <div style="font-size: 0.68rem; color: var(--text-muted); line-height: 1.35;">
            Standardized Soil Moisture Anomaly (SMA) from NOAA CPC / SPoRT-LIS measuring root-zone drought. Severe drought compresses the autumn color duration and causes early leaf drop.
          </div>
          <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #7f1d1d 0%, #d97706 25%, #eab308 42%, #cbd5e1 52%, #22c55e 75%, #2563eb 100%); border: 1px solid rgba(255,255,255,0.12);"></div>
          <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: var(--text-muted); font-weight: 700;">
            <span style="color: #f87171;">D4 Exceptional</span>
            <span style="color: #fbbf24;">D2 Severe</span>
            <span style="color: #cbd5e1;">Normal</span>
            <span style="color: #4ade80;">Moist</span>
            <span style="color: #60a5fa;">Surplus</span>
          </div>
        </div>
      `;
    } else if (viewMode === 'species') {
      const selectedSpecies: Array<'maple' | 'aspen' | 'oak' | 'larch'> = state.foliageSpeciesSelected || ['maple', 'aspen', 'oak', 'larch'];
      
      const speciesList: Array<{ id: 'maple' | 'aspen' | 'oak' | 'larch'; label: string; descTitle: string; color: string; desc: string }> = [
        {
          id: 'maple',
          label: '🔴 Scarlet Red',
          descTitle: 'Scarlet & Crimson Maples',
          color: '#d32f2f',
          desc: 'Sugar Maple, Red Maple, Scarlet Oak'
        },
        {
          id: 'aspen',
          label: '💛 Canary Gold',
          descTitle: 'Canary Yellow & Gold',
          color: '#ffc107',
          desc: 'Quaking Aspen, Bigleaf Maple, Birch'
        },
        {
          id: 'oak',
          label: '🪵 Bronze Copper',
          descTitle: 'Bronze & Copper Orange',
          color: '#e65100',
          desc: 'White/Oregon/Gambel Oak, Beech, Alder'
        },
        {
          id: 'larch',
          label: '🌲 Golden Larch',
          descTitle: 'Golden Larch & Tamarack',
          color: '#ffd54f',
          desc: 'Subalpine Larch (Larix lyallii), Tamarack'
        }
      ];

      const chipsHtml = speciesList.map(sp => {
        const isChecked = selectedSpecies.includes(sp.id);
        const activeBorder = isChecked ? `1px solid ${sp.color}` : '1px solid rgba(255,255,255,0.12)';
        const activeBg = isChecked ? `${sp.color}35` : 'rgba(15,23,42,0.6)';
        const activeColor = isChecked ? '#ffffff' : 'rgba(255,255,255,0.45)';
        const statusIcon = isChecked ? '✓' : '○';
        const statusColor = isChecked ? sp.color : 'rgba(255,255,255,0.3)';

        return `
          <button 
            class="gw-foliage-species-toggle ${isChecked ? 'active' : ''}" 
            data-subtype="${sp.id}" 
            title="Toggle ${sp.descTitle}" 
            style="display: flex; align-items: center; justify-content: space-between; gap: 6px; padding: 6px 8px; font-size: 0.68rem; font-weight: 700; border-radius: 6px; border: ${activeBorder}; background: ${activeBg}; color: ${activeColor}; cursor: pointer; transition: all 0.15s ease; user-select: none; box-sizing: border-box; text-align: left;">
            <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${sp.label}</span>
            <span style="font-size: 0.72rem; font-weight: 900; color: ${statusColor};">${statusIcon}</span>
          </button>
        `;
      }).join('');

      const activeSpeciesList = speciesList.filter(s => selectedSpecies.includes(s.id));
      let activeDescHtml = '';
      if (activeSpeciesList.length === 0) {
        activeDescHtml = '<div style="font-size: 0.68rem; color: var(--text-muted); font-style: italic;">No species layers enabled. Click any species above to display its native distribution on the map.</div>';
      } else {
        activeDescHtml = activeSpeciesList.map(s => `
          <div style="font-size: 0.66rem; color: #fff; line-height: 1.35; display: flex; align-items: flex-start; gap: 4px;">
            <span style="color: ${s.color}; font-weight: 800;">●</span>
            <div><strong>${s.descTitle}:</strong> <span style="color: var(--text-muted);">${s.desc}</span></div>
          </div>
        `).join('');
      }

      modeContentHtml = `
        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 0.78rem; font-weight: 700; color: #fff;">🌲 Tree Species Extent</span>
            <div style="display: flex; gap: 4px;">
              <button class="gw-foliage-species-batch-btn" data-action="all" style="padding: 2px 7px; font-size: 0.62rem; font-weight: 700; border-radius: 4px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); color: var(--text-muted); cursor: pointer; transition: all 0.15s ease;">
                All
              </button>
              <button class="gw-foliage-species-batch-btn" data-action="none" style="padding: 2px 7px; font-size: 0.62rem; font-weight: 700; border-radius: 4px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); color: var(--text-muted); cursor: pointer; transition: all 0.15s ease;">
                None
              </button>
            </div>
          </div>
          <div style="font-size: 0.65rem; color: var(--text-muted);">
            Multi-select species groups to independently overlay their native ranges:
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 5px;">
            ${chipsHtml}
          </div>
          <div style="display: flex; flex-direction: column; gap: 4px; padding-top: 2px;">
            ${activeDescHtml}
          </div>
        </div>
      `;
    }

    return `
      <div id="gw-foliage-panel-container" style="display: flex; flex-direction: column; gap: 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <!-- View Mode Switcher -->
        ${viewModeHtml}

        <!-- POI Profile Header -->
        <div style="background: rgba(15, 23, 42, 0.75); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 6px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              <div style="font-size: 0.70rem; text-transform: uppercase; font-weight: 700; color: var(--text-muted); letter-spacing: 0.5px;">Point of Interest</div>
              <div style="font-size: 0.85rem; font-weight: 700; color: #fff;">
                📍 ${lat.toFixed(3)}°N, ${lon.toFixed(3)}°W
              </div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 0.70rem; text-transform: uppercase; font-weight: 700; color: var(--text-muted);">Elevation</div>
              <div style="font-size: 0.85rem; font-weight: 700; color: #ffb300;">
                ${Math.round(elev / 3.28084)}m (${Math.round(elev)}ft)
              </div>
            </div>
          </div>

          <!-- Peak Prediction Banner -->
          <div style="background: linear-gradient(90deg, rgba(230, 74, 25, 0.25), rgba(211, 47, 47, 0.25)); border: 1px solid rgba(230, 74, 25, 0.5); border-radius: 6px; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between; margin-top: 2px;">
            <div style="font-size: 0.78rem; font-weight: 700; color: #ffab91; display: flex; align-items: center; gap: 5px;">
              <span>🍁</span>
              <span>${profile.peakDateRangeLabel}</span>
            </div>
            <span style="font-size: 0.70rem; font-weight: 700; color: #fff; background: rgba(0,0,0,0.4); padding: 2px 6px; border-radius: 4px;">
              ${profile.currentStage} (${profile.currentFpi}%)
            </span>
          </div>

          <!-- Seasonal Vibrancy Departure Pill -->
          <div id="gw-foliage-vibrancy-pill" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 5px 8px; display: flex; align-items: center; justify-content: space-between; margin-top: 2px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600; display: flex; align-items: center; gap: 4px;">
              <span>✨</span> <span>Seasonal Vibrancy:</span>
            </span>
            <span id="gw-foliage-vibrancy-val" style="font-size: 0.70rem; font-weight: 700; color: ${
              (profile.vibrancyCategory || '').includes('Above') ? '#ff8a80' : 
              (profile.vibrancyCategory || '').includes('Below') ? '#bcaaa4' : '#ffd54f'
            }; background: rgba(0,0,0,0.3); padding: 1px 6px; border-radius: 4px;">
              ${profile.vibrancyCategory || 'Average / Typical'}
            </span>
          </div>

          <!-- Peak Arrival Date Pill -->
          <div id="gw-foliage-peak-arrival-pill" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 5px 8px; display: flex; align-items: center; justify-content: space-between; margin-top: 2px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600; display: flex; align-items: center; gap: 4px;">
              <span>📅</span> <span>Peak Arrival Date:</span>
            </span>
            <span id="gw-foliage-peak-arrival-val" style="font-size: 0.70rem; font-weight: 700; color: #ffd54f; background: rgba(0,0,0,0.3); padding: 1px 6px; border-radius: 4px;">
              ${profile.peakStartDate || 'Mid October'}
            </span>
          </div>
        </div>

        ${modeContentHtml}
      </div>
    `;
  }

  public bindEvents(container: HTMLElement): void {
    this.container = container;

    // View mode switcher
    container.querySelectorAll('.gw-foliage-mode-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const mode = target.getAttribute('data-mode') as any;
        if (mode) {
          const updates: any = { foliageViewMode: mode };
          if (mode === 'peak_date') {
            updates.foliageTreedOnly = false;
          }
          store.update(updates);
        }
      });
    });

    // Multi-select species toggles
    container.querySelectorAll('.gw-foliage-species-toggle').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const subtype = target.getAttribute('data-subtype') as 'maple' | 'aspen' | 'oak' | 'larch';
        if (subtype) {
          const current = store.getState().foliageSpeciesSelected || ['maple', 'aspen', 'oak', 'larch'];
          let next: Array<'maple' | 'aspen' | 'oak' | 'larch'>;
          if (current.includes(subtype)) {
            next = current.filter(s => s !== subtype);
          } else {
            next = [...current, subtype];
          }
          store.update({ foliageSpeciesSelected: next });
        }
      });
    });

    // Batch toggle all / none
    container.querySelectorAll('.gw-foliage-species-batch-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const action = target.getAttribute('data-action');
        if (action === 'all') {
          store.update({ foliageSpeciesSelected: ['maple', 'aspen', 'oak', 'larch'] });
        } else if (action === 'none') {
          store.update({ foliageSpeciesSelected: [] });
        }
      });
    });

    // Coverage scope switching (Full Landscape vs Treed Regions Only)
    container.querySelectorAll('.gw-foliage-coverage-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const coverage = (btn as HTMLElement).getAttribute('data-coverage');
        const treedOnly = coverage === 'treed';
        store.update({ foliageTreedOnly: treedOnly });
      });
    });

    // Month tabs switching
    container.querySelectorAll('.gw-foliage-month-tab').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const monthIdx = parseInt(target.getAttribute('data-month-idx') || '1', 10);
        store.update({ foliageMonthIndex: monthIdx });
      });
    });

    // Day cell click -> update selected date and map layer
    container.querySelectorAll('.gw-foliage-day-cell').forEach(cell => {
      cell.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const dateStr = target.getAttribute('data-date');
        if (dateStr) {
          store.update({ foliageSelectedDate: dateStr, foliageViewMode: 'forecast' });
        }
      });
    });

    // Asynchronous PMTiles pixel sampling for exact raster map fidelity
    const state = store.getState();
    const manifest = state.manifest;
    const poi = state.selectedPoi;
    const defaultInfo = FoliageService.getDefaultDateInfo();
    const monthIndex = state.foliageMonthIndex !== undefined ? state.foliageMonthIndex : defaultInfo.monthIndex;
    const viewMode = state.foliageViewMode || 'peak_date';
    const targetProduct = state.foliageTreedOnly ? 'foliage_treed' : 'foliage';

    if (manifest) {
      const lat = poi ? poi.lat : (state.mapCenter ? state.mapCenter[1] : 45.52);
      const lon = poi ? poi.lng : (state.mapCenter ? state.mapCenter[0] : -122.67);

      // Asynchronously sample exact peak arrival date from foliage_peak_date.pmtiles
      const treedOnly = !!state.foliageTreedOnly;
      FoliageService.samplePeakDatePixel(manifest, lat, lon, treedOnly).then(sampled => {
        if (sampled) {
          const valEl = container.querySelector('#gw-foliage-peak-arrival-val');
          if (valEl) {
            valEl.textContent = sampled.dateLabel;
            (valEl as HTMLElement).style.color = sampled.colorHex;
          }
        }
      });

      // Asynchronously sample exact seasonal vibrancy from foliage_vibrancy.pmtiles / foliage_vibrancy_treed.pmtiles
      FoliageService.sampleVibrancyPixel(manifest, lat, lon, treedOnly).then(sampled => {
        if (sampled) {
          const valEl = container.querySelector('#gw-foliage-vibrancy-val');
          if (valEl) {
            valEl.textContent = sampled.category;
            (valEl as HTMLElement).style.color = sampled.colorHex;
          }
        }
      });

      if (viewMode === 'forecast') {
        FoliageService.sampleMonthTilePixels(
          manifest,
          monthIndex,
          lat,
          lon,
          2026,
          (dateStr, sampled) => {
            const cell = container.querySelector(`.gw-foliage-day-cell[data-date="${dateStr}"]`);
            if (cell) {
              (cell as HTMLElement).style.background = sampled.colorHex;
              const badgeEl = cell.querySelector('.gw-foliage-day-badge');
              if (badgeEl) {
                badgeEl.textContent = sampled.badgeLabel;
              }
            }
          },
          targetProduct
        );
      }
    }
  }

  public updateUi(): void {
    const cardBody = (this.container && this.container.classList.contains('legend-card-body'))
      ? this.container
      : (document.querySelector('.legend-card[data-layer="foliage"] .legend-card-body') as HTMLElement | null);
    if (!cardBody) return;
    const state = store.getState();
    cardBody.innerHTML = this.render(state);
    this.bindEvents(cardBody);
  }
}

export default FoliagePanel;
